import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { VdjPost, VdjComfort, vdjColor, vdjBeatChroma, VDJ_PRESETS, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le ciel n'a pas de projecteurs.
export const RIG = 'forbidden';

export const PROFILE = {
  id: 'hyperspace',
  family: 'particles',
  danceability: 0.90,
  requiresTier: 0,
  energyLevel: 0.88,
  bpmMin: 120,
  bpmMax: 180,
  movement: 0.95,
  tonality: 0.40,
  enabled: true
};

// HYPERSPACE : le saut en vitesse lumière, en boucle et en rythme. Trance,
// techno, EDM, drum'n'bass, synthwave. LOOK VDJ party (lib/vdjLook.js).
//
// LES PHASES. CROISIÈRE (champ d'étoiles 3D qui défile lentement, le système
// de la dernière sortie au loin) -> CHARGE (les étoiles s'étirent, la caméra
// tremble de plus en plus, un anneau d'énergie se forme au centre) -> SAUT
// (traits radiaux, cœur blanc, bord bleu devant, rouge derrière, flash) ->
// TUNNEL (bruit en coordonnées cylindriques qui défile, filaments bleus et
// violets, éclairs sur les kicks) -> SORTIE (les traits se rétractent d'un
// coup, onde de choc, le nouveau système en gros plan) -> CROISIÈRE.
// Avec une structure (adapt.dropReady) : CHARGE = la montée (16 temps avant
// le drop), SAUT = le drop, TUNNEL tant que l'intensité tient (4 à 16
// mesures), SORTIE sur une mesure. Sans structure : un cycle par scène de
// 20 s (R20) - croisière 0-9 s, charge 9-12, saut au premier temps après 12,
// tunnel, sortie au premier temps après 19.
//
// LES ÉTOILES : un seul buffer, 4 sommets par étoile ; le vertex shader
// projette la tête (z) et la queue (z + longueur) et tend un quad entre les
// deux, élargi en PIXELS (R1 : cœur de 1,2 à 2,4 px, gaine, halo qui tombe à
// 0 avant le bord du quad, R12). Aucun upload par image : z défile par
// `uDist` (modulo de la profondeur), la longueur par `uLen`.
//
// LE FOND en demi-résolution (HalfFloat, LinearFilter, R2) : le tunnel (bruit
// de valeur PÉRIODIQUE en angle - aucune couture - étiré le long de la
// profondeur) ou la nébuleuse du système (fbm, chaîne de domaine bornée,
// R14) ; recomposé en pleine résolution avec un dither d'1/255 (aucune
// bande). Les étoiles et le système sont nets par-dessus.
//
// LES SYSTÈMES : un quad plein écran ANALYTIQUE en pleine résolution, alpha
// prémultiplié (la planète cache les étoiles, les lueurs s'ajoutent) : soleil
// simple ou double (disque assombri au limbe, halo sous le seuil de bloom),
// géante gazeuse à bandes (fbm sur la sphère, bandes parallèles aux
// anneaux), tellurique avec océans, nuages et diffusion au limbe, lune,
// anneaux (densité par rayon, ombre de la planète sur les anneaux et des
// anneaux sur la planète par intersection rayon-sphère / rayon-plan),
// ceinture d'astéroïdes (cellules polaires dans le plan, 1 à 2,5 px). Tout
// éclairé par la direction du soleil du système. Composition au centre de la
// scène (R19) ; le masque safeZone atténue ce qui passe derrière la pochette.
// Six gabarits, une nouvelle combinaison à chaque sortie (couleurs tirées de
// la pochette par vdjColor, R4 ; palettes de secours sans pochette).
//
// Debug : ?hyperphase=cruise|charge|jump|tunnel|exit (tient la phase),
// ?hypersystem=<n> (épingle le gabarit 0-5).

const PHASES = ['cruise', 'charge', 'jump', 'tunnel', 'exit'];
const DEPTH = 6.0, NEAR = 0.12, SPREAD = 3.0, FOCAL = 1.0;
const SCENE_S = 20;
const SAFE_FLOOR = 0.35;
const FALLBACK = [[0x2bd8ff, 0xff2bd6, 0xffc21a], [0x7b2bff, 0x2bff8c, 0xff7a2b], [0x3c6cff, 0xff4a3c, 0xfff06a]];
// the systems: planet 0 none / 1 gas / 2 rock; rings; suns; belt; nebula; moon
const TEMPLATES = [
  { name: 'géante annelée', planet: 1, R: 0.62, rings: 1, suns: 1, belt: 0, neb: 0.5, moon: 0 },
  { name: 'tellurique', planet: 2, R: 0.56, rings: 0, suns: 1, belt: 0, neb: 0.3, moon: 1 },
  { name: 'soleil double', planet: 0, R: 0.0, rings: 0, suns: 2, belt: 1, neb: 0.8, moon: 0 },
  { name: 'nébuleuse', planet: 1, R: 0.42, rings: 0, suns: 1, belt: 0, neb: 1.0, moon: 0 },
  { name: 'tellurique annelée', planet: 2, R: 0.48, rings: 1, suns: 2, belt: 0, neb: 0.4, moon: 0 },
  { name: 'géante de glace', planet: 1, R: 0.58, rings: 1, suns: 1, belt: 1, neb: 0.2, moon: 1 }
];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function smooth(x) { x = clamp01(x); return x * x * (3 - 2 * x); }
function urlParam(name) { if (typeof location === 'undefined') return null; return new URLSearchParams(location.search).get(name); }
function mulberry(seed) { let a = seed >>> 0; return function () { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
// a star's colour from its temperature class (rough blackbody, linear)
function starColor(r) {
  if (r < 0.10) return [0.62, 0.72, 1.00];   // O/B blue
  if (r < 0.30) return [0.82, 0.88, 1.00];   // A white-blue
  if (r < 0.62) return [1.00, 0.97, 0.92];   // F/G white-yellow
  if (r < 0.86) return [1.00, 0.82, 0.58];   // K orange
  return [1.00, 0.62, 0.42];                 // M red
}

// --- shared GLSL --------------------------------------------------------------------------
const NOISE = `
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float hash3(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
  float vnoise3(vec3 p) {
    vec3 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float a = mix(mix(hash3(i), hash3(i + vec3(1.0, 0.0, 0.0)), f.x), mix(hash3(i + vec3(0.0, 1.0, 0.0)), hash3(i + vec3(1.0, 1.0, 0.0)), f.x), f.y);
    float b = mix(mix(hash3(i + vec3(0.0, 0.0, 1.0)), hash3(i + vec3(1.0, 0.0, 1.0)), f.x), mix(hash3(i + vec3(0.0, 1.0, 1.0)), hash3(i + vec3(1.0, 1.0, 1.0)), f.x), f.y);
    return mix(a, b, f.z);
  }
  float fbm3(vec3 p) { float v = 0.0, a = 0.5; for (int k = 0; k < 4; k++) { v += a * vnoise3(p); p = p * 2.03 + vec3(1.7, 9.2, 4.1); a *= 0.5; } return v / 0.9375; }
  // value noise periodic in x (period per): no seam around the tunnel
  float pnoise(vec2 p, float per) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    float x0 = mod(i.x, per), x1 = mod(i.x + 1.0, per);
    return mix(mix(hash2(vec2(x0, i.y)), hash2(vec2(x1, i.y)), f.x), mix(hash2(vec2(x0, i.y + 1.0)), hash2(vec2(x1, i.y + 1.0)), f.x), f.y);
  }
  float pfbm(vec2 p, float per) { float v = 0.0, a = 0.5; for (int k = 0; k < 4; k++) { v += a * pnoise(p, per); p *= 2.0; per *= 2.0; p.y += 3.1; a *= 0.5; } return v / 0.9375; }
  // the same with octaves faded out by oct (0..4): the fine ones alias near the vanishing point, where a turn is a few pixels
  float pfbmL(vec2 p, float per, float oct) { float v = 0.0, a = 0.5, w = 0.0; for (int k = 0; k < 4; k++) { float g = clamp(oct - float(k), 0.0, 1.0); v += a * g * pnoise(p, per); w += a * g; p *= 2.0; per *= 2.0; p.y += 3.1; a *= 0.5; } return w > 0.0 ? v / w : 0.5; }
`;
const SAFE_FRAG = `
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
`;
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- the background: tunnel / nebula, half resolution ----------------------------------------
const bgFragment = `
  precision highp float;
  ${NOISE}
  uniform float uAspect, uTime, uTunnel, uNeb, uTravel, uRot, uBolt, uBoltA, uResY, uNebSeed;
  uniform vec2 uShake;
  uniform vec3 uTc0, uTc1, uNc0, uNc1;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0) + uShake;
    vec3 col = vec3(0.0);
    if (uNeb > 0.001) {
      // the nebula: two fbm layers, a bounded domain warp (R14), continuous density
      vec3 p = vec3(s * 0.9, uNebSeed);
      vec3 w = vec3(fbm3(p * 1.3 + vec3(0.0, 0.0, uTime * 0.01)), fbm3(p * 1.3 + vec3(5.2, 1.3, 2.0)), 0.0) - 0.5;
      float d1 = fbm3(p * 1.6 + w * 0.35);
      float d2 = fbm3(p * 3.4 + w * 0.6 + vec3(2.0, 7.0, 0.0));
      float dens = smoothstep(0.42, 0.85, d1) * (0.55 + 0.6 * d2);
      float fil = smoothstep(0.55, 0.9, d2) * smoothstep(0.35, 0.7, d1);
      vec3 neb = mix(uNc0, uNc1, smoothstep(0.3, 0.8, d2)) * (1.0 - exp(-dens * 1.6)) * 0.42 + uNc1 * fil * 0.18;
      col += neb * uNeb * (1.0 - uTunnel);
    }
    if (uTunnel > 0.001) {
      float r = length(s);
      float ang = atan(s.y, s.x);
      float depth = 0.45 / max(r, 0.14);              // bounded: near the vanishing point the noise would alias into blocks at half resolution
      float u = ang / 6.2831853 + 0.5 + uRot / 6.2831853 + depth * 0.06;
      float z = depth * 0.55 + uTravel;
      // octaves allowed by the size of a turn on screen (2 pi r in half-res pixels / 18 cells)
      float turnPx = 6.2831853 * r * uResY * 0.5;
      float oct = clamp(log2(turnPx / (18.0 * 6.0)) + 1.0, 1.0, 4.0);
      float n1 = pfbmL(vec2(u * 18.0, z * 0.9), 18.0, oct);
      float n2 = pfbmL(vec2(u * 7.0 + 3.0, z * 0.45), 7.0, oct + 1.0);
      float fil = pow(smoothstep(0.42, 0.95, n1), 1.6);
      vec3 tc = mix(uTc0, uTc1, smoothstep(0.3, 0.75, n2));
      float wall = smoothstep(0.10, 0.34, r) * (1.0 - 0.35 * smoothstep(0.9, 1.9, r));
      vec3 t = tc * (0.10 + 1.25 * fil) * wall;
      // the vanishing point: a white-blue glow far ahead
      t += vec3(0.55, 0.7, 1.0) * exp(-r * 6.0) * 0.6;
      // the bolts on the kicks: a thin wandering line around the tunnel, a constant pixel width (R1)
      float da = abs(fract(u - uBoltA + 0.02 * sin(z * 9.0) + 0.5) - 0.5) * 6.2831853 * r;   // arc distance, height units
      float dpx = da * uResY * 0.5;
      t += vec3(0.75, 0.82, 1.0) * uBolt * (exp(-dpx * dpx / 2.0) * 1.2 + exp(-dpx / 6.0) * 0.25) * smoothstep(0.05, 0.3, r);
      col = mix(col, t, uTunnel);
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;
const bgComposeFragment = `
  precision highp float;
  ${SAFE_FRAG}
  uniform sampler2D uBg; uniform vec2 uScreen; uniform float uAspect, uGain;
  varying vec2 vUv;
  float h(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
  void main() {
    vec3 c = texture2D(uBg, vUv).rgb;
    c += (h(gl_FragCoord.xy) - 0.5) / 255.0;                 // no banding on the soft gradients
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    gl_FragColor = vec4(max(c, 0.0) * safeAtt(s) * uGain, 1.0);
  }
`;

// --- the stars: one buffer, streaks in the vertex shader --------------------------------------
const starsVertex = `
  precision highp float;
  attribute vec4 aStar;      // x, y, z0, magnitude 0..1
  attribute vec3 aCol;
  attribute vec2 aCorner;    // end (0 head, 1 tail), side (-1, 1)
  uniform float uDist, uLen, uAspect, uTwinkle, uTime, uDim, uShift;
  uniform vec2 uScreen, uShake;
  uniform vec2 uSafeCenter, uSafeHalfSize; uniform float uSafeFeather;
  varying vec3 vCol; varying float vAlong, vAcross, vLen, vCore, vBright, vT, vSafe;
  void main() {
    float zz = mod(aStar.z - uDist, ${DEPTH.toFixed(1)}) + ${NEAR.toFixed(2)};
    float zt = zz + uLen;
    vec2 sh = aStar.xy * ${FOCAL.toFixed(1)} / zz + uShake, st = aStar.xy * ${FOCAL.toFixed(1)} / zt + uShake;
    float ppu = uScreen.y * 0.5;                      // pixels per height unit
    vec2 Ph = sh * ppu, Pt = st * ppu;
    vec2 dir = Pt - Ph; float len = length(dir);
    vec2 d = len > 0.01 ? dir / len : vec2(1.0, 0.0);
    vec2 n = vec2(-d.y, d.x);
    float core = 0.6 + 1.2 * aStar.w;                 // core radius, px (R1: 1.2 to 2.4 px wide)
    float hw = core + 5.0;                            // the quad's half width: the halo reaches 0 inside it (R12)
    float end = aCorner.x;
    vec2 P = mix(Ph, Pt, end) + d * (end < 0.5 ? -hw : hw) + n * aCorner.y * hw;
    gl_Position = vec4(P.x / (uScreen.x * 0.5), P.y / (uScreen.y * 0.5), 0.0, 1.0);
    vAlong = end < 0.5 ? -hw : len + hw; vAcross = aCorner.y * hw; vLen = len; vCore = core;
    // brightness: magnitude, distance, the fade at both ends of the depth, the twinkle
    float fade = (1.0 - smoothstep(${(DEPTH * 0.55).toFixed(2)}, ${DEPTH.toFixed(1)}, zz))   // smoothstep needs edge0 < edge1 (reversed edges are undefined: 0 here)
       * smoothstep(${NEAR.toFixed(2)}, ${(NEAR + 0.25).toFixed(2)}, zz);
    float tw = 1.0 + uTwinkle * 0.6 * sin(uTime * 7.0 + aStar.x * 91.0 + aStar.y * 57.0);
    vBright = (0.25 + 0.95 * aStar.w * aStar.w) * fade * tw * uDim * (0.55 + 0.45 / max(zz, 0.4));
    vCol = aCol; vT = end;
    vec2 s = sh;
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    vSafe = mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
  }
`;
const starsFragment = `
  precision highp float;
  uniform float uShift, uGain;
  varying vec3 vCol; varying float vAlong, vAcross, vLen, vCore, vBright, vT, vSafe;
  void main() {
    float da = max(0.0, -vAlong) + max(0.0, vAlong - vLen);
    float dist = length(vec2(da, vAcross));
    float core = 1.0 - smoothstep(vCore - 0.5, vCore + 0.5, dist);
    float sheath = exp(-max(dist - vCore, 0.0) / 1.6) * 0.55;
    float halo = exp(-dist / 3.0) * 0.18 * (1.0 - smoothstep(4.0, 5.5, dist)) * (1.0 - smoothstep(6.0, 40.0, vLen));   // no halo on long streaks: 4000 of them would fog the frame
    // the Doppler shift along the streak: blue in front (the head), red behind (the tail)
    float t = clamp(vAlong / max(vLen, 1.0), 0.0, 1.0);
    vec3 shift = mix(vec3(0.35, 0.55, 1.0), vec3(1.0, 0.3, 0.25), t);
    vec3 tint = mix(vCol, shift, uShift);
    // a long streak spreads the star's light along its length: the core stays white, the colour carries the speed
    float spread = 1.0 / (1.0 + vLen / 70.0);
    // brighter toward the head, fading to the tail
    float taper = mix(1.0, 0.25, t);
    vec3 c = (mix(tint, vec3(1.0), 0.7) * core * (0.25 + 0.75 * spread) * taper + tint * (sheath * (0.3 + 0.7 * spread) + halo)) * vBright;
    gl_FragColor = vec4(c * vSafe * uGain, 1.0);
  }
`;

// --- the system and the effects: one analytic full-resolution quad, premultiplied alpha ---------
const sysFragment = `
  precision highp float;
  ${NOISE}
  ${SAFE_FRAG}
  uniform vec2 uScreen, uShake;
  uniform float uAspect, uTime, uGain, uVis, uScale, uLon;
  uniform vec4 uKind;               // planet (0 none, 1 gas, 2 rock), rings, suns, belt
  uniform float uR, uTiltS, uTiltC, uRoll, uMoon, uSeed;
  uniform vec3 uSun0, uSun1;        // xy, radius (height units, at scale 1)
  uniform vec3 uSunC0, uSunC1, uP0, uP1, uP2, uAtm;
  uniform float uRingE, uRingR, uShock, uFlash;
  uniform vec3 uRingC;
  varying vec2 vUv;
  vec3 rot(vec3 p) { float c = cos(uRoll), s = sin(uRoll); return vec3(c * p.x + s * p.y, -s * p.x + c * p.y, p.z); }
  // ring-plane density at radius rr (planet radii)
  float ringDens(float rr) {
    if (uKind.y < 0.5 || rr < 1.28 || rr > 2.25) return 0.0;
    float b = vnoise3(vec3(rr * 26.0, uSeed, 0.0)) * 0.6 + vnoise3(vec3(rr * 90.0, uSeed + 3.0, 0.0)) * 0.4;
    float gap = smoothstep(0.012, 0.03, abs(rr - 1.78));        // a Cassini division
    return (0.25 + 0.75 * b) * gap * smoothstep(1.28, 1.36, rr) * (1.0 - smoothstep(2.1, 2.25, rr));
  }
  void main() {
    vec2 s0 = (gl_FragCoord.xy - 0.5 * uScreen) / uScreen.y * 2.0;    // height units, y up, centred
    vec2 s = s0 + uShake;
    float px = 2.0 / uScreen.y;                                       // one pixel in height units
    vec3 glow = vec3(0.0); vec3 col = vec3(0.0); float cov = 0.0;
    if (uVis > 0.002) {
      vec2 q = s / uScale; float pq = px / uScale;
      // the light: from the sun(s), screen direction lifted toward the viewer
      vec3 L = normalize(vec3(uSun0.xy, 0.9));
      if (uKind.z > 1.5) L = normalize(vec3((uSun0.xy + uSun1.xy) * 0.5, 0.9));
      if (uKind.x < 0.5) L = normalize(vec3(0.0, 0.0, 1.0));
      // suns: limb-darkened discs, a halo under the bloom threshold
      for (int k = 0; k < 2; k++) {
        if (float(k) >= uKind.z) break;
        vec3 sn = k == 0 ? uSun0 : uSun1; vec3 sc = k == 0 ? uSunC0 : uSunC1;
        float d = length(q - sn.xy) / sn.z;
        float disc = 1.0 - smoothstep(1.0 - pq / sn.z, 1.0 + pq / sn.z, d);
        float limb = sqrt(max(0.0, 1.0 - d * d));
        vec3 dc = mix(sc, vec3(1.0), 0.65) * (0.75 + 0.5 * limb) * 1.25;
        col = mix(col, dc, disc); cov = max(cov, disc);
        glow += sc * (exp(-max(d - 1.0, 0.0) * 1.4) * 0.30 + exp(-max(d - 1.0, 0.0) * 0.25) * 0.05);
      }
      // the planet frame
      vec3 P2 = rot(vec3(q, 0.0));
      float R = uR;
      // ring plane: z = -y cosT / sinT (lower half toward the viewer)
      float zr = -P2.y * uTiltC / max(uTiltS, 0.05);
      float rrPlane = length(vec2(P2.x, P2.y / max(uTiltS, 0.05)));
      // the belt (in the same plane, around the planet or the centre)
      if (uKind.w > 0.5) {
        float br0 = R > 0.0 ? R * 2.5 : 0.55, br1 = R > 0.0 ? R * 3.1 : 0.85;
        if (rrPlane > br0 - 0.02 && rrPlane < br1 + 0.02) {
          float ang = atan(P2.y / max(uTiltS, 0.05), P2.x);
          float N = 220.0, M = 5.0;
          float ai = floor((ang / 6.2831853 + 0.5) * N), ri = floor((rrPlane - br0) / (br1 - br0) * M);
          for (int di = -1; di <= 1; di++) {
            float a2 = ai + float(di);
            float h = hash2(vec2(mod(a2, N), ri) + uSeed);
            if (h > 0.45) continue;
            float ac = (a2 + 0.2 + 0.6 * hash2(vec2(a2, ri + 7.0))) / N * 6.2831853 - 3.14159265;
            float rc = br0 + (ri + 0.2 + 0.6 * hash2(vec2(a2 + 3.0, ri))) / M * (br1 - br0);
            vec2 pc = vec2(cos(ac) * rc, sin(ac) * rc * uTiltS);
            float rpx = 1.0 + 1.5 * hash2(vec2(a2, ri + 11.0));
            float dd = length(P2.xy - pc) / pq;
            float rock = 1.0 - smoothstep(rpx - 0.5, rpx + 0.5, dd);
            float zc = -pc.y * uTiltC / max(uTiltS, 0.05);
            bool hidden = R > 0.0 && length(pc) < R && zc < sqrt(max(0.0, R * R - dot(pc, pc)));
            if (!hidden && rock > 0.0) { vec3 rc3 = mix(vec3(0.42, 0.36, 0.30), vec3(0.75, 0.70, 0.62), hash2(vec2(a2, ri + 5.0))) * (0.35 + 0.65 * max(0.0, dot(normalize(vec3(-pc, 0.6)), -L) * 0.5 + 0.5)); col = mix(col, rc3, rock); cov = max(cov, rock); }
          }
        }
      }
      // rings behind the planet
      float r2 = dot(P2.xy, P2.xy), r = sqrt(r2);
      float zs = R > 0.0 ? sqrt(max(0.0, R * R - r2)) : 0.0;
      float rd = R > 0.0 ? ringDens(rrPlane / R) : 0.0;
      vec3 P3 = vec3(P2.x, P2.y, zr);
      float ringShadow = 1.0;
      if (rd > 0.0) {
        // the planet's shadow on the rings (ray toward the sun hits the sphere)
        float tl = dot(-P3, L);
        if (tl > 0.0 && length(P3 + L * tl) < R) ringShadow = 0.18;
      }
      vec3 ringCol = mix(uP2, vec3(0.9, 0.85, 0.75), 0.5) * (0.35 + 0.65 * ringShadow) * 0.85;
      float ringA = rd * 0.85 * (1.0 - smoothstep(0.0, 0.6 * pq, 0.0));
      bool ringFront = zr > zs;
      if (rd > 0.0 && !(r < R && !ringFront)) { if (!(r < R)) { col = mix(col, ringCol, ringA); cov = max(cov, ringA); } }
      // the planet
      if (R > 0.0) {
        float pc = 1.0 - smoothstep(R - pq, R + pq, r);
        vec3 n = vec3(P2.xy, zs) / R;
        float lam = dot(n, L);
        float lit = smoothstep(-0.08, 0.3, lam);
        vec3 surf;
        if (uKind.x < 1.5) {
          // gas giant: bands parallel to the rings, turbulent edges
          vec3 axis = vec3(0.0, uTiltC, uTiltS);
          float lat = dot(n, axis);
          float lon = atan(dot(n, cross(axis, vec3(1.0, 0.0, 0.0))), n.x) + uLon;
          float warp = fbm3(vec3(lat * 9.0, lon * 1.4, uSeed)) - 0.5;
          float band = sin(lat * 18.0 + warp * 3.2 * (1.0 - smoothstep(0.75, 0.97, abs(lat))) + uSeed) * 0.5 + 0.5;
          float pole = 1.0 - smoothstep(0.75, 0.97, abs(lat));          // the longitude pinches at the poles: the fine turbulence fades there
          float fine = mix(0.5, fbm3(vec3(lat * 40.0 + warp * 2.0, lon * 3.0, uSeed + 4.0)), pole);
          surf = mix(mix(uP0, uP1, band), uP2, smoothstep(0.55, 0.8, fine) * 0.6);
          // a storm
          vec2 st = vec2(lon - 1.2, lat + 0.25); st.x = mod(st.x + 3.14159, 6.28318) - 3.14159;
          surf = mix(surf, uP2 * 1.2, (1.0 - smoothstep(0.08, 0.14, length(st * vec2(0.5, 1.6)))) * 0.7);
        } else {
          // rock: oceans, continents, clouds
          vec3 np = vec3(cos(uLon) * n.x - sin(uLon) * n.z, n.y, sin(uLon) * n.x + cos(uLon) * n.z);
          float land = fbm3(np * 2.6 + uSeed);
          float cloud = fbm3(np * 4.5 + vec3(uTime * 0.02, 0.0, uSeed + 2.0));
          vec3 ocean = uP0 * 0.45, ground = mix(uP1, uP2, smoothstep(0.55, 0.75, land)) * 0.8;
          surf = mix(ocean, ground, smoothstep(0.50, 0.53, land));
          surf = mix(surf, vec3(0.92), smoothstep(0.58, 0.75, cloud) * 0.85);
        }
        // the rings' shadow on the planet (ray toward the sun crosses the ring plane inside the rings)
        float sh = 1.0;
        if (uKind.y > 0.5) {
          vec3 N = vec3(0.0, uTiltC, uTiltS);
          vec3 Ps = vec3(P2.xy, zs);
          float den = dot(L, N);
          if (abs(den) > 1e-3) { float tt = -dot(Ps, N) / den; if (tt > 0.0) sh = 1.0 - 0.7 * ringDens(length(Ps + L * tt) / R); }
        }
        vec3 pcol = surf * (0.02 + 1.05 * lit * sh);
        // the atmosphere: scattering at the limb, on the lit side
        float rim = pow(1.0 - clamp(n.z, 0.0, 1.0), 2.6) * smoothstep(-0.25, 0.45, lam);
        pcol += uAtm * rim * (uKind.x > 1.5 ? 0.9 : 0.45);
        col = mix(col, pcol, pc); cov = max(cov, pc);
        // the halo outside the limb (under the bloom threshold)
        float lside = clamp(dot(normalize(P2.xy + 1e-4), L.xy) * 0.5 + 0.5, 0.0, 1.0);
        glow += uAtm * exp(-max(r - R, 0.0) / (0.035 * R)) * (1.0 - pc) * lside * (uKind.x > 1.5 ? 0.55 : 0.25);
        // the moon
        if (uMoon > 0.5) {
          vec2 mc = vec2(R * 1.55, R * 0.62); float mr = R * 0.14;
          float dm = length(P2.xy - mc);
          float mcov = 1.0 - smoothstep(mr - pq, mr + pq, dm);
          if (mcov > 0.0) { vec3 mn = vec3((P2.xy - mc) / mr, sqrt(max(0.0, 1.0 - dm * dm / (mr * mr)))); float ml = smoothstep(-0.05, 0.3, dot(mn, L)); vec3 mcol = vec3(0.55, 0.53, 0.5) * (0.75 + 0.25 * fbm3(mn * 4.0 + uSeed)) * (0.02 + ml); col = mix(col, mcol, mcov); cov = max(cov, mcov); }
        }
        // rings in front of the planet
        if (rd > 0.0 && r < R && ringFront) { col = mix(col, ringCol, ringA); cov = max(cov, ringA); }
      }
      col *= uVis; glow *= uVis; cov *= uVis;
    }
    // the energy ring of the charge, the shockwave of the exit: lines with a pixel width (R1)
    float rs = length(s0);
    if (uRingE > 0.002) {
      float dpx = abs(rs - uRingR) / px;
      glow += (vec3(1.0) * (1.0 - smoothstep(0.6, 1.6, dpx)) * 0.9 + uRingC * (exp(-dpx / 3.0) * 0.7 + exp(-dpx / 10.0) * 0.3)) * uRingE;
    }
    if (uShock >= 0.0 && uShock < 1.2) {
      float rad = uShock * 2.2;
      float dpx = abs(rs - rad) / px;
      float fade = 1.0 - smoothstep(0.4, 1.2, uShock);
      glow += (vec3(1.0) * (1.0 - smoothstep(1.0, 2.5, dpx)) * 0.9 + vec3(0.45, 0.65, 1.0) * exp(-dpx / 5.0) * 0.6 + vec3(1.0, 0.4, 0.3) * exp(-abs(rs - rad - 8.0 * px) / px / 4.0) * 0.35) * fade;
    }
    glow += vec3(1.0) * uFlash;
    float att = safeAtt(s0) * uGain;
    gl_FragColor = vec4((col * cov + glow) * att, cov);
  }
`;

export class HyperspaceVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    this._nStars = this._tier === 0 ? 2000 : 4000;
    this._post = new VdjPost({ bloomStrength: 0.12, bloomThreshold: 0.78, grain: 0.015, vignette: 0.3 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._adapt = new Adapt();
    this._clock = new SceneClock({ period: SCENE_S, start: 0 });
    this._rng = mulberry(0x48595045);
    const fp = urlParam('hyperphase'); this._forcedPhase = PHASES.includes(fp) ? fp : null;
    const fs = urlParam('hypersystem'); this._forcedSys = fs != null && fs !== '' && isFinite(Number(fs)) ? Math.max(0, Math.min(TEMPLATES.length - 1, Math.floor(Number(fs)))) : null;
    this._decisions = [];
  }
  get postProcessing() { return this._post.value; }
  _say(what) { this._decisions.push({ t: +this._time.toFixed(2), what }); if (this._decisions.length > 40) this._decisions.shift(); }

  init(scene) {
    this._scene = scene;
    this._time = 0; this._phase = 'cruise'; this._phaseT = 0; this._beatCount = 0; this._barCount = 0; this._beatPeriod = 60 / 128;
    this._dist = 0; this._v = 0.3; this._len = 0; this._tunnel = 0; this._travel = 0; this._rot = 0; this._rotSpeed = 1.6; this._rotTarget = 1.6;
    this._bolt = 0; this._boltA = 0; this._shock = -1; this._flash = 0; this._ringE = 0; this._sysVis = 1; this._sysScale = 1; this._lumSince = 0;
    this._tunnelBars = 0; this._pendingNew = false; this._sysCount = 0; this._order = [0, 1, 2, 3, 4, 5]; this._palIdx = 0; this._palette = null; this._artUrl = undefined;
    this._size = new THREE.Vector2(); this._aspect = 16 / 9;
    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._shared = { uScreen: { value: new THREE.Vector2(1920, 1080) }, uAspect: { value: 16 / 9 }, uShake: { value: new THREE.Vector2() }, uTime: { value: 0 }, uGain: { value: 1 } };
    // the background, half resolution
    this._rtBg = new THREE.WebGLRenderTarget(960, 540, { type: THREE.HalfFloatType, depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter });
    this._bgU = Object.assign({ uTunnel: { value: 0 }, uNeb: { value: 0.5 }, uTravel: { value: 0 }, uRot: { value: 0 }, uBolt: { value: 0 }, uBoltA: { value: 0 }, uResY: { value: 540 }, uNebSeed: { value: 0 },
      uTc0: { value: new THREE.Color(0.18, 0.35, 1.0) }, uTc1: { value: new THREE.Color(0.55, 0.2, 1.0) }, uNc0: { value: new THREE.Color(0.6, 0.2, 0.8) }, uNc1: { value: new THREE.Color(0.2, 0.6, 1.0) } },
      { uAspect: this._shared.uAspect, uShake: this._shared.uShake, uTime: this._shared.uTime });
    this._bgMat = new THREE.ShaderMaterial({ uniforms: this._bgU, vertexShader: quadVertex, fragmentShader: bgFragment, depthTest: false, depthWrite: false });
    this._passScene = new THREE.Scene(); this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._bgMat); this._passQuad.frustumCulled = false; this._passScene.add(this._passQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    const composeU = Object.assign({ uBg: { value: this._rtBg.texture } }, { uScreen: this._shared.uScreen, uAspect: this._shared.uAspect, uGain: this._shared.uGain }, safe);
    this._bgQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: composeU, vertexShader: quadVertex, fragmentShader: bgComposeFragment, depthTest: false, depthWrite: false }));
    this._bgQuad.frustumCulled = false; this._bgQuad.renderOrder = -1000;
    const self = this; this._bgQuad.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._bgQuad);
    // the stars
    const N = this._nStars, rnd = mulberry(0x5747A2);
    const star = new Float32Array(N * 4 * 4), colA = new Float32Array(N * 4 * 3), corner = new Float32Array(N * 4 * 2), idx = new Uint32Array(N * 6);
    for (let i = 0; i < N; i++) {
      // a uniform disc in x, y (no star exactly on the axis: it would never move), uniform z
      let x, y; do { x = (rnd() * 2 - 1) * SPREAD; y = (rnd() * 2 - 1) * SPREAD; } while (Math.hypot(x, y) < 0.08);
      const z = rnd() * DEPTH, mag = Math.pow(rnd(), 2.2), c = starColor(rnd());
      for (let k = 0; k < 4; k++) {
        const v = i * 4 + k;
        star.set([x, y, z, mag], v * 4); colA.set(c, v * 3);
        corner.set([k < 2 ? 0 : 1, k % 2 === 0 ? -1 : 1], v * 2);
      }
      idx.set([i * 4, i * 4 + 1, i * 4 + 2, i * 4 + 1, i * 4 + 3, i * 4 + 2], i * 6);
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('aStar', new THREE.BufferAttribute(star, 4)); g.setAttribute('aCol', new THREE.BufferAttribute(colA, 3)); g.setAttribute('aCorner', new THREE.BufferAttribute(corner, 2));
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(N * 4 * 3), 3)); g.setIndex(new THREE.BufferAttribute(idx, 1));
    g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._starU = Object.assign({ uDist: { value: 0 }, uLen: { value: 0 }, uTwinkle: { value: 0 }, uDim: { value: 1 }, uShift: { value: 0 } }, this._shared, safe);
    this._stars = new THREE.Mesh(g, new THREE.ShaderMaterial({ uniforms: this._starU, vertexShader: starsVertex, fragmentShader: starsFragment, depthTest: false, depthWrite: false, transparent: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));   // (end, side) -> (direction, normal) winds every quad clockwise: FrontSide would cull them all
    this._stars.frustumCulled = false; this._stars.renderOrder = -900; scene.add(this._stars);
    // the system and the effects
    this._sysU = Object.assign({ uVis: { value: 1 }, uScale: { value: 1 }, uLon: { value: 0 }, uKind: { value: new THREE.Vector4(1, 1, 1, 0) }, uR: { value: 0.6 }, uTiltS: { value: 0.3 }, uTiltC: { value: 0.95 }, uRoll: { value: 0.2 }, uMoon: { value: 0 }, uSeed: { value: 1 },
      uSun0: { value: new THREE.Vector3(-1.2, 0.6, 0.06) }, uSun1: { value: new THREE.Vector3(1.2, 0.5, 0.04) }, uSunC0: { value: new THREE.Color(1, 0.85, 0.6) }, uSunC1: { value: new THREE.Color(0.6, 0.75, 1) },
      uP0: { value: new THREE.Color(0.8, 0.6, 0.4) }, uP1: { value: new THREE.Color(0.6, 0.35, 0.25) }, uP2: { value: new THREE.Color(0.95, 0.85, 0.7) }, uAtm: { value: new THREE.Color(0.4, 0.6, 1.0) },
      uRingE: { value: 0 }, uRingR: { value: 0.1 }, uShock: { value: -1 }, uFlash: { value: 0 }, uRingC: { value: new THREE.Color(0.4, 0.6, 1) } }, this._shared, safe);
    this._sys = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._sysU, vertexShader: quadVertex, fragmentShader: sysFragment, depthTest: false, depthWrite: false, transparent: true,
      blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcAlphaFactor, blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor }));
    this._sys.frustumCulled = false; this._sys.renderOrder = -800; scene.add(this._sys);
    this._newSystem(true);
  }

  // --- the palette and the systems ---------------------------------------------------------------
  _paletteFrom(profile) {
    const v = profile.artworkPalette && profile.artworkPalette.vividColors;
    const hexes = Array.isArray(v) && v.length >= 2 ? v.slice(0, 3) : FALLBACK[this._palIdx % FALLBACK.length];
    return hexes.map((h) => vdjColor(typeof h === 'string' ? parseInt(h.replace('#', ''), 16) : h));
  }
  _newSystem(first) {
    if (!first) { this._sysCount++; if (this._pendingNew) { this._palIdx++; this._pendingNew = false; } }
    if (this._sysCount % TEMPLATES.length === 0) { for (let i = this._order.length - 1; i > 0; i--) { const j = Math.floor(this._rng() * (i + 1)); [this._order[i], this._order[j]] = [this._order[j], this._order[i]]; } }
    const ti = this._forcedSys != null ? this._forcedSys : this._order[this._sysCount % TEMPLATES.length];
    const T = TEMPLATES[ti]; const r = this._rng; const U = this._sysU;
    this._template = ti;
    const pal = this._palette || FALLBACK[0].map((h) => vdjColor(h));
    const hsl = (c, s, l, dh) => { const o = {}; c.getHSL(o); return new THREE.Color().setHSL((o.h + (dh || 0) + 1) % 1, s, l).convertSRGBToLinear(); };
    U.uKind.value.set(T.planet, T.rings, T.suns, this._tier === 0 && T.planet === 0 ? 1 : T.belt);
    U.uR.value = T.R; U.uMoon.value = T.moon; U.uSeed.value = r() * 50;
    const tilt = 0.18 + r() * 0.22; U.uTiltS.value = Math.sin(tilt); U.uTiltC.value = Math.cos(tilt); U.uRoll.value = (r() - 0.5) * 0.5;
    // the planet's colours from the palette (desaturated toward believable)
    const base = pal[Math.floor(r() * pal.length)];
    if (ti === 5) { U.uP0.value = hsl(new THREE.Color(0.5, 0.8, 1), 0.35, 0.62, 0); U.uP1.value = hsl(new THREE.Color(0.5, 0.8, 1), 0.45, 0.45, 0.05); U.uP2.value = hsl(new THREE.Color(0.5, 0.8, 1), 0.2, 0.8, -0.05); }
    else if (T.planet === 2) { U.uP0.value = hsl(new THREE.Color(0.2, 0.4, 1), 0.65, 0.42, 0); U.uP1.value = hsl(base, 0.35, 0.36, 0.1); U.uP2.value = hsl(base, 0.25, 0.55, -0.08); }
    else { U.uP0.value = hsl(base, 0.45, 0.58, 0); U.uP1.value = hsl(base, 0.55, 0.36, 0.06); U.uP2.value = hsl(base, 0.3, 0.78, -0.06); }
    U.uAtm.value = T.planet === 2 ? new THREE.Color(0.25, 0.5, 1.0) : hsl(base, 0.5, 0.6, 0.02);
    // the suns: off to a side (single), or a pair around the centre (binary without a planet)
    const side = r() < 0.5 ? -1 : 1;
    if (T.suns === 1) { U.uSun0.value.set(side * (1.05 + r() * 0.3), 0.45 + r() * 0.25, 0.05 + r() * 0.03); }
    else if (T.planet === 0) { U.uSun0.value.set(-0.16, 0.04, 0.075); U.uSun1.value.set(0.17, -0.03, 0.05); }
    else { U.uSun0.value.set(side * 1.15, 0.55, 0.05); U.uSun1.value.set(side * 1.38, 0.38, 0.03); }
    const temp = [new THREE.Color(1.0, 0.82, 0.55), new THREE.Color(0.65, 0.78, 1.0), new THREE.Color(1.0, 0.55, 0.35), new THREE.Color(1.0, 0.95, 0.85)];
    U.uSunC0.value.copy(temp[Math.floor(r() * temp.length)]); U.uSunC1.value.copy(temp[Math.floor(r() * temp.length)]);
    // the nebula
    this._nebTarget = this._tier === 0 ? 0 : T.neb;
    this._bgU.uNebSeed.value = r() * 40;
    this._bgU.uNc0.value.copy(hsl(pal[0], 0.75, 0.5, 0)); this._bgU.uNc1.value.copy(hsl(pal[1 % pal.length], 0.75, 0.55, 0));
    this._say('système ' + ti + ' (' + T.name + ')');
  }

  // --- the phases ---------------------------------------------------------------------------------
  _enter(phase) {
    if (this._phase === phase) return;
    this._phase = phase; this._phaseT = 0; this._say('phase ' + phase);
    if (phase === 'jump') { if (this._comfort.requestFlash(this._time)) this._flash = VDJ_DROP_FLASH * 0.7; this._tunnelBars = 0; }
    if (phase === 'tunnel') this._tunnelBars = 0;
    if (phase === 'exit') { this._shock = 0; this._newSystem(false); this._sysVis = 1; this._sysScale = 1.35; if (this._comfort.requestFlash(this._time)) this._flash = 0.35; }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._clock.update(dt); this._adapt.update(dt, profile);
    if ((profile.artworkUrl || null) !== this._artUrl) { this._artUrl = profile.artworkUrl || null; this._palette = this._paletteFrom(profile); }
    if (paused) return;                                   // frozen, never black
    this._time += dt; this._phaseT += dt;
    const a = this._adapt.get();
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 128;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bp = a.beatPhase != null ? a.beatPhase : 0;
    const beat = Math.pow(1 - bp, 3) * Math.max(0.35, a.pulse);
    if (a.beat) this._beatCount++;
    if (a.bar) this._barCount++;
    if (a.phrase) { this._pendingNew = true; this._say('phrase : nouvelle palette à la prochaine sortie'); }
    const director = profile.director || {};
    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const P = this._phase, t = this._clock.t;
    // THE PHASE MACHINE
    if (this._forcedPhase) {
      // held for the debug: a phase loops on itself (jump -> jump), the exit replays every 2 bars with a new system
      if (P !== this._forcedPhase) this._enter(this._forcedPhase);
      if (this._forcedPhase === 'exit' && this._phaseT > this._beatPeriod * 8) { this._phase = 'cruise'; this._enter('exit'); }
    } else if (a.dropReady) {
      if (P === 'cruise' && beatsToDrop != null && beatsToDrop <= 16) this._enter('charge');
      else if ((P === 'cruise' || P === 'charge') && a.drop) this._enter('jump');
      else if (P === 'charge' && beatsToDrop == null && this._phaseT > this._beatPeriod * 20) this._enter('cruise');
      else if (P === 'cruise' && this._phaseT > SCENE_S * 2 && a.bar) this._enter('charge');        // a long calm still gets its jump
      else if (P === 'charge' && this._phaseT > this._beatPeriod * 16 && beatsToDrop == null && a.beat) this._enter('jump');
    } else {
      // no structure: one cycle per 20 s scene (R20)
      if (P === 'cruise' && t >= 9 && t < 12) this._enter('charge');
      else if (P === 'charge' && t >= 12 && a.beat) this._enter('jump');
      else if (P === 'charge' && t >= 13) this._enter('jump');
      else if (P === 'tunnel' && (t >= 19 || t < 9) && a.beat) this._enter('exit');
    }
    if (this._phase === 'jump' && this._phaseT >= this._beatPeriod * (this._forcedPhase ? 1e9 : 1)) this._enter('tunnel');
    if (this._phase === 'tunnel' && !this._forcedPhase && a.dropReady) {
      if (a.bar) this._tunnelBars++;
      if (a.bar && this._tunnelBars >= 4 && (a.intensity < 0.55 || this._tunnelBars >= 16)) this._enter('exit');
    }
    if (this._phase === 'exit' && this._phaseT >= this._beatPeriod && !this._forcedPhase) this._enter('cruise');
    const ph = this._phase, pt = this._phaseT;
    // SPEED and STREAK LENGTH by phase
    let vT, lenT;
    const chargeK = ph === 'charge' ? smooth(pt / (this._beatPeriod * 16)) : 0;
    if (ph === 'cruise') { vT = 0.3 + 0.2 * a.intensity; lenT = 0.0; }
    else if (ph === 'charge') { vT = 0.4 + 3.5 * chargeK; lenT = 0.02 + 0.35 * chargeK * chargeK; }
    else if (ph === 'jump') { vT = 60; lenT = 1.6 * (1 + 0.45 * beat); }
    else if (ph === 'tunnel') { vT = 40; lenT = 1.1 * (1 + 0.5 * beat); }
    else { vT = 0.4; lenT = 0; }
    this._v += (vT - this._v) * Math.min(1, dt / (ph === 'exit' ? 0.05 : (ph === 'jump' ? 0.12 : 0.4)));
    // the streaks grow with the speed, retract AT ONCE at the exit
    const tauL = lenT < this._len ? (ph === 'exit' || ph === 'cruise' ? 0.05 : 0.15) : 0.2;
    this._len += (lenT - this._len) * Math.min(1, dt / tauL);
    this._dist += this._v * dt;
    // the tunnel
    const tunT = ph === 'tunnel' ? 1 : (ph === 'jump' ? smooth(pt / this._beatPeriod) * 0.6 : 0);
    this._tunnel += (tunT - this._tunnel) * Math.min(1, dt / (ph === 'exit' ? 0.08 : 0.35));
    if (ph === 'tunnel' && a.bar) { this._rotTarget = -this._rotTarget; this._say('tunnel : rotation inversée'); }
    this._rotSpeed += (this._rotTarget - this._rotSpeed) * Math.min(1, dt / (this._beatPeriod * 0.5));
    this._rot += this._rotSpeed * dt * (0.5 + 0.5 * this._tunnel);
    this._travel += dt * (2.0 + 1.5 * beat) * this._tunnel;
    if (ph === 'tunnel' && a.beat) { this._bolt = 1; this._boltA = this._rng(); }
    this._bolt = Math.max(0, this._bolt - dt / 0.16);
    // the system: recedes in the charge, gone in the jump, close-up at the exit, a slow survey in the cruise
    const visT = ph === 'cruise' || ph === 'exit' ? 1 : (ph === 'charge' ? 1 - 0.6 * chargeK : 0);
    this._sysVis += (visT - this._sysVis) * Math.min(1, dt / (ph === 'jump' ? 0.12 : 0.4));
    const scaleT = ph === 'charge' ? 1 - 0.45 * chargeK : (ph === 'cruise' ? 1.0 + 0.08 * smooth(pt / 30) : this._sysScale);
    this._sysScale += (scaleT - this._sysScale) * Math.min(1, dt / (ph === 'cruise' && pt < this._beatPeriod * 8 ? 1.6 : 0.5));
    this._sysU.uLon.value += dt * 0.025;
    // the charge ring and the camera shake
    const ringT = ph === 'charge' ? 0.25 + 0.75 * chargeK : 0;
    this._ringE += (ringT - this._ringE) * Math.min(1, dt / 0.2);
    const shakeA = ph === 'charge' ? 0.014 * chargeK * chargeK : (ph === 'jump' ? 0.01 * (1 - pt / this._beatPeriod) : 0);
    this._shared.uShake.value.set(shakeA * (Math.sin(this._time * 47.0) + 0.5 * Math.sin(this._time * 83.0)), shakeA * (Math.sin(this._time * 53.0 + 1.0) + 0.5 * Math.sin(this._time * 71.0)));
    if (this._shock >= 0) { this._shock += dt / 0.9; if (this._shock > 1.2) this._shock = -1; }
    // the nebula follows the system's visibility
    const nebT = (this._nebTarget || 0) * this._sysVis;
    this._bgU.uNeb.value += (nebT - this._bgU.uNeb.value) * Math.min(1, dt / 0.6);
    // uniforms
    const pal = this._palette || FALLBACK[0].map((h) => vdjColor(h));
    this._shared.uTime.value = this._time;
    this._starU.uDist.value = this._dist; this._starU.uLen.value = this._len;
    this._starU.uTwinkle.value = ph === 'cruise' ? beat : 0;
    this._starU.uShift.value = clamp01((this._v - 2.0) / 25.0);
    this._starU.uDim.value = 1 - 0.7 * this._tunnel;
    this._bgU.uTunnel.value = this._tunnel; this._bgU.uTravel.value = this._travel; this._bgU.uRot.value = this._rot; this._bgU.uBolt.value = this._bolt; this._bgU.uBoltA.value = this._boltA;
    this._bgU.uTc0.value.setRGB(0.18, 0.35, 1.0).lerp(pal[0], 0.15); this._bgU.uTc1.value.setRGB(0.55, 0.2, 1.0).lerp(pal[1 % pal.length], 0.15);
    this._sysU.uVis.value = this._sysVis; this._sysU.uScale.value = this._sysScale;
    this._sysU.uRingE.value = this._ringE; this._sysU.uRingR.value = 0.07 + 0.05 * beat + 0.04 * (1 - chargeK); this._sysU.uRingC.value.copy(pal[0]);
    this._sysU.uShock.value = this._shock; this._sysU.uFlash.value = this._flash; this._flash = Math.max(0, this._flash - dt / 0.12);
    // R10: the mean luminance from the layers
    const est = 0.04 + 0.22 * this._tunnel + 0.08 * this._sysVis * (this._bgU.uNeb.value + 0.4) + 0.5 * this._sysU.uFlash.value + 0.06 * clamp01(this._len);
    this._lumSince += dt; if (this._lumSince > 0.25) { this._shared.uGain.value = this._comfort.measure(est, this._lumSince); this._lumSince = 0; }
    this._post.update(vdjBeatChroma(bp, this._beatPeriod) * (0.4 + 0.6 * clamp01(this._v / 20)) + 0.3);
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y; if (prev) { W = prev.width; H = prev.height; }
    this._shared.uScreen.value.set(W, H); this._shared.uAspect.value = W / Math.max(1, H);
    const hw = Math.max(2, W >> 1), hh = Math.max(2, H >> 1);
    if (this._rtBg.width !== hw || this._rtBg.height !== hh) this._rtBg.setSize(hw, hh);
    this._bgU.uResY.value = hh;
    renderer.setRenderTarget(this._rtBg); renderer.render(this._passScene, this._ortho);
    renderer.setRenderTarget(prev);
  }

  get probe() {
    return { tier: this._tier, stars: this._nStars, phase: this._phase, phaseT: +this._phaseT.toFixed(2), v: +this._v.toFixed(2), len: +this._len.toFixed(3), tunnel: +this._tunnel.toFixed(2), system: this._template, sysVis: +this._sysVis.toFixed(2), scene: +this._clock.t.toFixed(2), gain: +this._shared.uGain.value.toFixed(2), decisions: this._decisions.slice(-8) };
  }

  dispose() {
    for (const m of [this._bgQuad, this._stars, this._sys]) if (m) { if (this._scene) this._scene.remove(m); m.geometry.dispose(); m.material.dispose(); }
    if (this._bgQuad) this._bgQuad.onBeforeRender = null;
    if (this._passQuad) this._passQuad.geometry.dispose();
    if (this._bgMat) this._bgMat.dispose();
    if (this._rtBg) this._rtBg.dispose();
    this._scene = null;
  }
}
